import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import './SearchableSelect.css';

export interface SearchableSelectOption {
  value: string;
  label: string;
  subLabel?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  emptyMessage?: string;
  disabled?: boolean;
  excludeSubLabelFromSearch?: boolean;
  highlightSubLabel?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  id,
  className = '',
  emptyMessage = 'No results found',
  disabled = false,
  excludeSubLabelFromSearch = false,
  highlightSubLabel = false
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(o => o.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autofocus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select(); // Highlight text for quick replacement
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      setSearchQuery('');
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const filterOption = (o: SearchableSelectOption) => {
    const term = searchQuery.toLowerCase();
    const labelMatch = o.label.toLowerCase().includes(term);
    if (excludeSubLabelFromSearch) {
      return labelMatch;
    }
    const subLabelMatch = o.subLabel ? o.subLabel.toLowerCase().includes(term) : false;
    return labelMatch || subLabelMatch;
  };

  // Keyboard controls
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter') {
      const filtered = options.filter(filterOption);
      if (filtered.length > 0) {
        handleSelect(filtered[0].value);
      }
    }
  };

  const filteredOptions = options.filter(filterOption);

  return (
    <div className={`searchable-select ${className}`} ref={containerRef} id={id}>
      {!isOpen ? (
        <button
          type="button"
          className={`searchable-select__trigger ${disabled ? 'searchable-select__trigger--disabled' : ''}`}
          onClick={handleToggle}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="searchable-select__label">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown size={16} className="searchable-select__arrow" />
        </button>
      ) : (
        <div className="searchable-select__input-wrapper">
          <Search size={14} className="searchable-select__search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="searchable-select__input"
            placeholder={placeholder}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}

      {isOpen && (
        <ul className="searchable-select__options" role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => (
              <li
                key={option.value}
                className={`searchable-select__option ${option.value === value ? 'searchable-select__option--selected' : ''}`}
                role="option"
                aria-selected={option.value === value}
                onClick={() => handleSelect(option.value)}
              >
                <span className="searchable-select__option-label">{option.label}</span>
                {option.subLabel && (
                  <span className={`searchable-select__option-sublabel ${highlightSubLabel ? 'searchable-select__option-sublabel--highlighted' : ''}`}>
                    {option.subLabel}
                  </span>
                )}
              </li>
            ))
          ) : (
            <li className="searchable-select__empty">{emptyMessage}</li>
          )}
        </ul>
      )}
    </div>
  );
}

export default SearchableSelect;
